
import { computed, defineComponent, ref } from "vue";
import { useRoute } from "vue-router";
import { useRouter } from "vue-router";
import { routeNames } from "@/router";
import { useSelectAttribute, AuthorOrder } from "@/lib/selectedAttribute";
import store from "@/stores";
import MiddleHeader from "@/components/MiddleHeader.vue";
import SelectAttribute from "@/components/SelectAttribute.vue";
import SelectCheckboxAttribute from "@/components/SelectCheckboxAttribute.vue";
import TemplateThumbnail from "@/components/TemplateThumbnail.vue";
import { ITemplateFilter, filterTemplate, consts } from "@/services/Models";
import Questionnaire from "@/components/Questionnaire.vue";
import { ITemplateSet } from "../services/Models";

export default defineComponent({
  name: "SearchTemplate",
  components: {
    SelectAttribute,
    SelectCheckboxAttribute,
    MiddleHeader,
    TemplateThumbnail,
    Questionnaire,
  },
  setup() {
    const route = useRoute();
    const router = useRouter();

    // アンケートから来たかどうか
    const fromQ = computed(() => route.query.fromQ !== undefined);
    const hideAttributeGroupID = computed(
      () => route.query.hideAttributeGroupID
    );
    const isCheckboxAttributeGroupID = computed(() => {
      // console.log("consts.checkboxAttrGroupID:[" + consts.checkboxAttrGroupID + "]");
      // console.log("helper:[" + JSON.stringify(helper) + "]");
      return consts.checkboxAttrGroupID;
    });
    const resource = store.useResource();

    const campaignID = route.params.campaignID as string;
    const campaign = resource.data.campaigns.filter(
      (x) => x.id.toString() == campaignID
    );
    // 各IDを数値に変換する
    const parseNumbersFromQuery = (args: string | (string | null)[] | null) => {
      const xs: number[] = [];
      if (args !== undefined) {
        if (Array.isArray(args)) {
          args.forEach((x) => xs.push(Number.parseInt(x?.toString() ?? "")));
        } else {
          xs.push(Number.parseInt(args?.toString() ?? ""));
        }
      }
      return xs;
    };
    // 作者フィルタ
    const authorFilters = computed<ITemplateFilter[]>(() => {
      const args = route.query.authorID;
      return parseNumbersFromQuery(args).map((authorID) => ({
        campaignID: 0,
        attributeID: undefined,
        authorID: authorID,
      }));
    });

    // 選択した属性ID群
    const attributeIDs = computed(() => {
      const args = route.query.attributeID;
      return parseNumbersFromQuery(args);
    });
    const helper = computed(() =>
      useSelectAttribute(resource.data.attributeGroups, attributeIDs.value)
    );
    // 属性の選択状態をテンプレフィルタに変換
    const filters = computed<ITemplateFilter[][]>(() => {
      return helper.value.groupWithList
        .filter((g) => g.group.id !== isCheckboxAttributeGroupID.value)
        .map((g) => {
          return g.selectedList.value.map((id) => ({
            campaignID: 0,
            attributeID: id,
            authorID: undefined,
          }));
        });
    });
    // オプション絞り込み属性の選択状態をテンプレフィルタに変換
    const optionFilters = computed<ITemplateFilter[]>(() => {
      const withList = helper.value.groupWithList.find(
        (g) => g.group.id === isCheckboxAttributeGroupID.value
      );
      if (withList !== undefined) {
        return withList.selectedList.value.map((id) => ({
          campaignID: 0,
          attributeID: id,
          authorID: undefined,
        }));
      } else {
        return [];
      }
    });

    // 属性でフィルタされたテンプレートに変換
    const tofilteredTemplates = (templates: ITemplateSet[]) => {
      const xs = templates
        .filter(
          // 作者フィルタで絞り込み
          (x) =>
            authorFilters.value.length == 0 ||
            authorFilters.value.some((f) => filterTemplate(x, f))
        )
        // キャンペーンフィルタで絞り込み
        .filter(
          (x) =>
            campaign.length == 0 ||
            campaign.some((c) => c.filters.some((f) => filterTemplate(x, f)))
        )
        // 作者がシステムのテンプレートは除く
        .filter((x) =>
          x.frames.some((frame) => frame.author.id != consts.systemAuthor)
        );
      // 選択された属性で絞り込み
      const filtered = filters.value.reduce((sets, fs) => {
        if (fs.length == 0) {
          return sets;
        }
        return sets.filter((x) => fs.some((f) => filterTemplate(x, f)));
      }, xs);
      // オプションで選択された属性で絞り込み
      const optionFiltered = filtered.filter((x) =>
        optionFilters.value.every((f) => filterTemplate(x, f))
      );
      return optionFiltered;
    };

    // 無名漫画家のテンプレート群
    const filteredTemplates = computed(() => {
      const templates = resource.data.templateSets.filter((x) =>
        x.frames.some((frame) => frame.author.isFamous == false) && x.deleteFlg == false
      );
      if (templates === undefined) return [];
      // 著者順に並べる
      return sortByAuthor(tofilteredTemplates(templates));
    });

    // 有名漫画家のテンプレート群
    const famousFilteredTemplates = computed(() => {
      const templates = resource.data.templateSets.filter((x) =>
        x.frames.some((frame) => frame.author.isFamous) && x.deleteFlg == false
      );
      if (templates === undefined) return [];
      // 著者順に並べる
      return sortByAuthor(tofilteredTemplates(templates));
    });

    const sortByAuthor = (templates: ITemplateSet[]): ITemplateSet[] => {
      const authorOrder = helper.value.authorOrder();
      if (authorOrder === AuthorOrder.ASC) {
        templates.sort((a, b) => a.frames[0].author.id - b.frames[0].author.id);
      } else {
        templates.sort((a, b) => b.frames[0].author.id - a.frames[0].author.id);
      }
      return templates;
    };

    const toSelectView = () => {
      router.push({ name: routeNames.SelectTemplate });
    };
    const reSelect = () => {
      isShowModal.value = true;
    };
    const allOpen = () => {
      document.getElementsByName("headertitle").forEach((x) => {
        var el = x as HTMLDetailsElement;
        el.open = true;
      });
    };
    const isShowModal = ref(false);
    return {
      helper,
      filteredTemplates,
      famousFilteredTemplates,
      toSelectView,
      reSelect,
      allOpen,
      isShowModal,
      fromQ,
      hideAttributeGroupID,
      isCheckboxAttributeGroupID,
    };
  },
});
